import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession
} from 'amazon-cognito-identity-js'
import { ref } from 'vue'

// Distinguish a GENUINE auth failure (the refresh token is revoked/expired/invalid
// — the user really is logged out) from a TRANSIENT failure (a network blip while
// Cognito refreshes an expired access token). Only the former should end the
// session; ejecting a real user on a momentary network error turns a recoverable
// glitch into a forced re-login. Anything we can't positively classify as genuine
// is treated as transient (fail-open: keep the user signed in, re-check later).
export const isGenuineAuthError = (err) => {
  if (!err) {
    return false
  }
  const code = err.code || err.name || ''
  const message = err.message || ''

  return (
    code === 'NotAuthorizedException' ||
    /Refresh Token has been revoked|Invalid Refresh Token|Refresh Token has expired|User does not exist|user is not authenticated/i.test(message)
  )
}

let cypressRunning
try {
  if (Cypress) {
    cypressRunning = true
  }
} catch (e) {
  cypressRunning = false
}

export default class CognitoAuth {
  constructor () {
    this.userSession = null
    if (cypressRunning) {
      this.configure({
        UserPoolId: Cypress.env('VUE_APP_COGNITO_USER_POOL_ID'),
        ClientId: Cypress.env('VUE_APP_COGNITO_USER_POOL_CLIENT_ID')
      })
    } else {
      this.configure({
        UserPoolId: process.env.VUE_APP_COGNITO_USER_POOL_ID,
        ClientId: process.env.VUE_APP_COGNITO_USER_POOL_CLIENT_ID
      })
    }
  }

  configure (options) {
    this.userPool = new CognitoUserPool({
      UserPoolId: options.UserPoolId,
      ClientId: options.ClientId
    })
  }

  isAuthenticated (cb) {
    const cognitoUser = this.getCurrentUser()
    if (cognitoUser != null) {
      cognitoUser.getSession((err) => {
        if (err) {
          return cb(err, false)
        }
        return cb(null, true)
      })
    } else {
      cb(null, false)
    }
  }

  async isAuthenticatedPromise () {
    return new Promise((resolve) => {
      try {
        const cognitoUser = this?.getCurrentUser()
        if (cognitoUser) {
          cognitoUser.getSession((err, session) => {
            if (!err && session) {
              resolve(session)
            } else {
              resolve(false)
            }
          })
        } else {
          resolve(false)
        }
      } catch (err) {
        Promise.reject(err)
      }
    })
  }

  // Resolve the session's health as a tri-state so callers can react to a network
  // blip differently from a real logout:
  //   'valid'     - getSession returned a live session
  //   'invalid'   - genuine auth failure (revoked/expired refresh token, no user)
  //   'transient' - getSession errored for a non-auth reason (e.g. network during
  //                 a token refresh) — do NOT log the user out; re-check later.
  async getSessionStatus () {
    return new Promise((resolve) => {
      const cognitoUser = this?.getCurrentUser()
      if (!cognitoUser) {
        resolve('invalid')
        return
      }
      cognitoUser.getSession((err, session) => {
        if (!err && session) {
          resolve('valid')
        } else if (isGenuineAuthError(err)) {
          resolve('invalid')
        } else {
          resolve('transient')
        }
      })
    })
  }

  changePassword (oldPassword, newPassword, cb) {
    const cognitoUser = this.getCurrentUser()

    // getSession() will load valid tokens cached in the local store so we can
    // authenticate and change the users password
    cognitoUser.getSession((err) => {
      if (err) {
        return cb(err, false)
      }
      cognitoUser.changePassword(oldPassword, newPassword, (changeErr, result) => {
        if (changeErr) {
          return cb(changeErr)
        }

        // Invalidate all sessions after successful password change for security
        cognitoUser.globalSignOut({
          onSuccess: () => {
            cb(null, result)
          },
          onFailure: (globalSignOutErr) => {
            console.error('Global sign out failed after password change:', globalSignOutErr)
            // Still report success since password was changed successfully
            cb(null, result)
          }
        })
      })
    })
  }

  // Updates user information
  async updateAttributes (firstname, lastname, phoneNumber, company) {
    const cognitoUser = this.getCurrentUser()

    const attributeList = [
      new CognitoUserAttribute({
        Name: 'given_name',
        Value: firstname
      }),
      new CognitoUserAttribute({
        Name: 'family_name',
        Value: lastname
      }),
      new CognitoUserAttribute({
        Name: 'phone_number',
        Value: phoneNumber
      }),
      new CognitoUserAttribute({
        Name: 'custom:organisation',
        Value: company
      })
      // ... other attributes if needed
    ]

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err) => {
        if (err) {
          reject(err)
        } else {
          cognitoUser.updateAttributes(attributeList, (error, result) => {
            if (error) {
              reject(error)
            } else {
              resolve(result)
            }
          })
        }
      })
    })
  }

  /**
   * Set a signed-in session from JWT tokens (used when authenticating via Auth Proxy API).
   * This avoids a direct Cognito network call for login from the browser.
   */
  setSessionFromTokens (username, tokens) {
    const email = (username || '').toLowerCase()
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: this.userPool
    })

    const session = new CognitoUserSession({
      IdToken: new CognitoIdToken({ IdToken: tokens?.idToken }),
      AccessToken: new CognitoAccessToken({ AccessToken: tokens?.accessToken }),
      RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens?.refreshToken || '' })
    })

    cognitoUser.setSignInUserSession(session)
  }

  /**
   * Logout of your cognito session.
   */
  logout () {
    if (this.getCurrentUser()) {
      this.getCurrentUser().signOut((err) => {
        console.log('revoke token error', err)
      })
    }
  }

  /**
   * Global sign out - invalidates all tokens and sessions across all devices
   */
  globalSignOut () {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCurrentUser()
      if (!cognitoUser) {
        return reject(new Error('No current user'))
      }

      cognitoUser.getSession((err) => {
        if (err) {
          return reject(err)
        }

        cognitoUser.globalSignOut({
          onSuccess: (result) => {
            resolve(result)
          },
          onFailure: (error) => {
            reject(error)
          }
        })
      })
    })
  }

  /**
   * Resolves the current token based on a user session. If there
   * is no session it returns null.
   * @param {*} cb callback
   */
  getIdToken (cb) {
    if (this.getCurrentUser() == null) {
      return cb(null, null)
    }
    this.getCurrentUser().getSession((err, session) => {
      if (err) return cb(err)
      if (session.isValid()) {
        return cb(null, session.getIdToken().getJwtToken())
      }
      cb(Error('Session is invalid'))
    })
  }

  getIdTokenPromise () {
    return new Promise((resolve, reject) => {
      if (this.getCurrentUser() == null) {
        return resolve(null)
      }
      this.getCurrentUser().getSession((err, session) => {
        if (err) {
          return reject(err)
        }
        if (session.isValid()) {
          return resolve(session.getIdToken().getJwtToken())
        }
        // getSession() auto-refreshes an expired access/id token, so reaching here
        // means the session genuinely can't be revived (e.g. revoked/expired refresh
        // token). Reject rather than leave the promise hanging forever.
        return reject(new Error('Session is invalid'))
      })
    })
  }

  // Force a token refresh via the refresh token and resolve the new id-token JWT.
  // getSession() only refreshes once the cached token is actually expired; this is
  // for the case where the server rejected a token the client still considered valid
  // (e.g. it expired in flight during a long request, or clock skew) — we want a
  // brand-new token before retrying. Rejects (does NOT swallow) so callers can tell a
  // genuine auth failure apart from a transient one via isGenuineAuthError.
  refreshSession () {
    return new Promise((resolve, reject) => {
      const cognitoUser = this.getCurrentUser()
      if (!cognitoUser) {
        return reject(new Error('No current user'))
      }
      cognitoUser.getSession((err, session) => {
        if (err) {
          return reject(err)
        }
        const refreshToken = session?.getRefreshToken?.()
        if (!refreshToken?.getToken?.()) {
          return reject(new Error('No refresh token'))
        }
        cognitoUser.refreshSession(refreshToken, (refreshErr, newSession) => {
          if (refreshErr) {
            return reject(refreshErr)
          }
          resolve(newSession.getIdToken().getJwtToken())
        })
      })
    })
  }

  getCurrentUser () {
    return this.userPool?.getCurrentUser()
  }

  async getUserAttributes () {
    const user = this.getCurrentUser()
    return await new Promise((resolve, reject) => {
      user.getSession((err) => {
        if (err) {
          reject(err)
        } else {
          user.getUserAttributes((err, result) => {
            if (err) {
              reject(err)
            } else {
              resolve(result)
            }
          })
        }
      })
    })
  }

  getSession () {
    return new Promise((resolve, reject) => {
      try {
        this.userPool.getCurrentUser().getSession((err, session) => {
          if (err) {
            return reject(err.message)
          }
          if (session.isValid()) {
            return resolve(session)
          }
        })
      } catch (err) {
        return reject(new Error('No current user'))
      }
    })
  }

  // very primitive change listener
  onChange = ref(() => {})
}

export const CognitoAuthPlugin = function (app) {
  let cypressRunning
  try {
    if (Cypress) {
      cypressRunning = true
    }
  } catch (e) {
    cypressRunning = false
  }

  const cognitoAuth = new CognitoAuth()
  if (cypressRunning) {
    cognitoAuth.configure({
      UserPoolId: Cypress.env('VUE_APP_COGNITO_USER_POOL_ID'),
      ClientId: Cypress.env('VUE_APP_COGNITO_USER_POOL_CLIENT_ID')
    })
  } else {
    cognitoAuth.configure({
      UserPoolId: process.env.VUE_APP_COGNITO_USER_POOL_ID,
      ClientId: process.env.VUE_APP_COGNITO_USER_POOL_CLIENT_ID
    })
  }

  // Expose as a global property
  app.config.globalProperties.$cognitoAuth = cognitoAuth
}
